// src/pages/Report/Report.jsx
import React, { useState } from "react";
import "../../styles/report.css";
import { getOrGenerateReportAPI, renderDocumentAPI } from "../../api/report_api";
import ReportDocumentView from "./ReportDocumentView";

// Pretty period label for display
function periodLabel(mode, week, month, startDate, endDate) {
  if (mode === "weekly"  && week)  return `Week ${week}`;
  if (mode === "monthly" && month) {
    const [y, m] = month.split("-");
    return new Date(y, m - 1, 1)
      .toLocaleString("en-IN", { month: "long", year: "numeric" });
  }
  if (startDate && endDate) {
    const s = new Date(startDate).toLocaleDateString("en-IN");
    const e = new Date(endDate  ).toLocaleDateString("en-IN");
    return `${s} — ${e}`;
  }
  return "";
}



export default function Report() {
  const email = localStorage.getItem("email");

  // filter
  const [mode,      setMode]      = useState("weekly");
  const [week,      setWeek]      = useState("");
  const [month,     setMonth]     = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");

  // UI
  const [loading,    setLoading]    = useState(false);
  const [report,     setReport]     = useState(null);
  const [status,     setStatus]     = useState("");
  const [dataUpTo,   setDataUpTo]   = useState(null);
  const [isComplete, setIsComplete] = useState(true);
  const [error,      setError]      = useState("");

  // word doc
  const [asDocument, setAsDocument] = useState(false);
  const [docHtml,    setDocHtml]    = useState("");
  const [showDoc,    setShowDoc]    = useState(false);

  function buildPayload() {
    if (mode === "weekly") {
      if (!week)  { setError("Please select a week");  return null; }
      return { period: "weekly", week };
    }
    if (mode === "monthly") {
      if (!month) { setError("Please select a month"); return null; }
      return { period: "monthly", month };
    }
    if (!startDate || !endDate) { setError("Select start & end date"); return null; }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date must be before end date"); return null;
    }
    return { period: "custom", start_date: startDate, end_date: endDate };
  }

  const handleGetReport = async () => {
    setError(""); setReport(null); setStatus("");
    setDataUpTo(null); setIsComplete(true);
    const payload = buildPayload();
    if (!payload) return;

    setLoading(true);
    try {
      // Always call get-or-generate first (fetch/generate the correct range)
      const data = await getOrGenerateReportAPI(email, payload);
      const fetchedReport = data.reports?.[0] || null;

      setReport(fetchedReport);
      setStatus(data.status     || "");
      setDataUpTo(data.data_up_to || null);
      setIsComplete(data.is_complete !== false);

      // If word-doc mode, call backend render-document with exact range
      if (asDocument && fetchedReport) {
        const startIso = fetchedReport.week_start;
        const endIso   = fetchedReport.week_end;
        const docData  = await renderDocumentAPI(email, startIso, endIso, fetchedReport.label);
        setDocHtml(docData.html);
        setShowDoc(true);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Open existing report as word doc — call backend for full classified view
  const handleViewAsDoc = async () => {
    if (!report) return;
    setLoading(true);
    try {
      // Pass raw stored values — backend will parse flexibly
      const startIso = report.week_start;
      const endIso   = report.week_end;
      const docData  = await renderDocumentAPI(email, startIso, endIso, report.label);
      setDocHtml(docData.html);
      setShowDoc(true);
    } catch (err) {
      setError("Failed to build document view. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  function StatusBanner() {
    if (!status) return null;
    const note = !isComplete && dataUpTo
      ? <span className="report-partial-note"> · Data up to <strong>{dataUpTo}</strong> (period ongoing)</span>
      : null;
    const msgs = {
      cached:    <>✅ Report loaded from saved data.{note}</>,
      generated: <>⚡ AI generated &amp; saved!{note}</>,
      updated:   <>🔄 Updated with latest data up to <strong>{dataUpTo}</strong>.</>,
    };
    return <div className="report-success">{msgs[status] || null}</div>;
  }

  return (
    <div className="report-wrapper">

      {/* HEADER */}
      <div className="report-header">
        <h1 className="report-page-title">Reflection Reports</h1>
        <p className="report-page-subtitle">
          Select a period and click <strong>Get Report</strong> — loads saved data or generates automatically.
        </p>
      </div>

      {/* FILTER CARD */}
      <div className="rfc-card">
        <div className="rfc-row">

          <div className="rfc-field">
            <label className="rfc-label">Period</label>
            <select className="rfc-input" value={mode}
              onChange={(e) => { setMode(e.target.value); setReport(null); setError(""); setStatus(""); }}>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {mode === "weekly" && (
            <div className="rfc-field">
              <label className="rfc-label">Select Week</label>
              <input type="week" className="rfc-input" value={week}
                onChange={(e) => setWeek(e.target.value)} />
            </div>
          )}

          {mode === "monthly" && (
            <div className="rfc-field">
              <label className="rfc-label">Select Month</label>
              <input type="month" className="rfc-input" value={month}
                onChange={(e) => setMonth(e.target.value)} />
            </div>
          )}

          {mode === "custom" && (
            <>
              <div className="rfc-field">
                <label className="rfc-label">Start Date</label>
                <input type="date" className="rfc-input" value={startDate}
                  onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="rfc-field">
                <label className="rfc-label">End Date</label>
                <input type="date" className="rfc-input" value={endDate}
                  onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </>
          )}

          <div className="rfc-btn-group">
            <button className="rfc-btn rfc-btn-generate" onClick={handleGetReport} disabled={loading}>
              {loading
                ? <><span className="rfc-spinner" /> Fetching…</>
                : "📊 Get Report"}
            </button>
          </div>
        </div>

        {/* Checkbox — shows word doc after fetch */}
        <div className="rfc-checkbox-row">
          <label className="rfc-checkbox-label">
            <input type="checkbox" checked={asDocument}
              onChange={(e) => setAsDocument(e.target.checked)} />
            &nbsp; Show as Word Document
          </label>
        </div>
      </div>

      {/* MESSAGES */}
      <StatusBanner />
      {error && <div className="report-error">{error}</div>}

      {/* RESULT */}
      <div className="report-box">
        <h2 className="report-section-title">
          Report
          {!isComplete && report && (
            <span className="report-partial-badge">Partial — ongoing</span>
          )}
        </h2>

        {/* Placeholder */}
        {!report && !error && !loading && !status && (
          <div className="report-placeholder">
            <span className="emoji">📊</span>
            <p>Select a period and click <strong>📊 Get Report</strong>.</p>
            <p className="report-hint">
              Saved reports load instantly. New periods are AI-generated automatically.
            </p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="report-placeholder">
            <span className="emoji">⏳</span>
            <p>Checking database… AI generating if needed…</p>
          </div>
        )}

        {/* ONE COMBINED REPORT CARD */}
        {report && !loading && (
          <div className="rpt-card">
            {/* Period header */}
            <div className="rpt-period">
              <span className="rpt-period-icon">
                {report.label === "weekly" ? "📅" : report.label === "monthly" ? "🗓️" : "📌"}
              </span>
              <span className="rpt-period-label">
                {(report.label === "weekly" || report.label === "monthly" || report.label === "custom")
                  && report.week_start && report.week_end && (
                  <>
                    {report.label === "weekly"  && <>Week: {new Date(report.week_start).toLocaleDateString("en-IN")} — {new Date(report.week_end).toLocaleDateString("en-IN")}</>}
                    {report.label === "monthly" && <>Month: {new Date(report.week_start).toLocaleString("en-IN",{month:"long",year:"numeric"})}</>}
                    {report.label === "custom"  && <>Custom: {new Date(report.week_start).toLocaleDateString("en-IN")} — {new Date(report.week_end).toLocaleDateString("en-IN")}</>}
                  </>
                )}
              </span>
              <span className={`rpt-badge rpt-badge-${report.label}`}>{report.label}</span>
            </div>

            {/* Summary */}
            <div className="rpt-summary">{report.summary}</div>

            {/* Footer */}
            <div className="rpt-meta">
              <span>Generated: {report.generated_at ? new Date(report.generated_at).toLocaleString("en-IN") : "—"}</span>
              {!isComplete && dataUpTo && (
                <span className="report-partial-note"> · Data up to {dataUpTo}</span>
              )}
              {/* View as word doc button */}
              <button className="rpt-doc-btn" onClick={handleViewAsDoc}>
                📄 View as Document
              </button>
            </div>
          </div>
        )}
      </div>

      {showDoc && <ReportDocumentView html={docHtml} onClose={() => setShowDoc(false)} />}
    </div>
  );
}